import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import Popup from '@ecp/common/src/components/modal/bo/Popup';

import { useConfirmDialog } from '@bo-hooks/common/useModal';
import memberCertInfoApi from '@bo-apis/member/memberCertInfoApi';
import { ContentTitle } from '@bo-components/common/styled';
import Grid from '@bo-components/common/grid/Grid';
import { useGridOptions, useEntryMeanValidators, ENT_MEAN_CODE } from '../hooks/MemberEntryMeanHooks';

const MemberEntryMeanPopup = ({ open, onClose, subTitle, entryMeanCode, siteNumber, subsidiaryList }) => {
  const gridRef = useRef(null);
  const { showConfirmDialog } = useConfirmDialog();

  const [entryMeanList, setEntryMeanList] = useState([]);
  const subsidiaryCodeList = useMemo(
    () => subsidiaryList.map(({ subsidiaryCompanyCode }) => subsidiaryCompanyCode),
    [subsidiaryList]
  );
  const gridOptions = useGridOptions({ entryMeanCode });
  const entryMeanValidators = useEntryMeanValidators({ entryMeanCode, subsidiaryCodeList, gridRef });

  const handleFirstRender = useCallback(async () => {
    const { result } = await memberCertInfoApi.getMemberCertInfoList(siteNumber);

    setEntryMeanList(result);
  }, [siteNumber]);

  const createMemberCertInfo = useCallback(
    async ({ created, deleted, updated }) => {
      await memberCertInfoApi.createMemberCertInfo({
        created: created?.map((item) => ({ ...item, siteNumber })),
        deleted,
        updated,
      });
      onClose();
    },
    [onClose, siteNumber]
  );

  const handleConfirmClick = useCallback(async () => {
    if (!(await gridRef.current.validate())) return;

    const { created, updated, deleted } = gridRef.current.getChangedData();

    if (created || updated || deleted) {
      showConfirmDialog(
        `저장하시겠습니까?\n(입력: ${created.length || 0}건, 수정: ${updated.length || 0}건, 삭제: ${
          deleted.length || 0
        }건)`,
        () => createMemberCertInfo({ created, updated, deleted })
      );
    } else {
      onClose();
    }
  }, [showConfirmDialog, createMemberCertInfo, onClose]);

  const closePopup = () => {
    if (gridRef.current.hasChangedData() > 0) {
      showConfirmDialog('입력중인 항목이 있습니다.\n저장없이 취소하시겠습니까?', onClose);
    } else {
      onClose();
    }
  };

  const initRow = useMemo(
    () => ({
      certMethodCode: entryMeanCode === ENT_MEAN_CODE.PIN ? 'P' : entryMeanCode === ENT_MEAN_CODE.EMAIL ? 'M' : '',
      certInfo: '',
      memberNumber: '',
      subsidiaryCode: '',
      memberCertInfoNo: '',
      siteNumber: '',
    }),
    [entryMeanCode]
  );

  return (
    <Popup
      type={'Large'}
      open={open}
      header={'인증정보관리'}
      onConfirmClick={handleConfirmClick}
      onClose={closePopup}
      onCancelClick={closePopup}
      confirmButtonText={'저장'}
    >
      <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
        <ContentTitle type={'secondary'} width={'100%'}>
          {subTitle}
        </ContentTitle>
        <Spacing top={'16px'} />
        {subsidiaryCodeList.length > 0 && (
          <Grid
            showDelete
            showAddRow
            unPaged
            initRow={initRow}
            height={'496px'}
            title={'인증정보관리'}
            excel={'excelWork'}
            options={gridOptions}
            total={entryMeanList.length}
            data={entryMeanList}
            onRenderFirstFinish={handleFirstRender}
            validators={entryMeanValidators}
            ref={gridRef}
          />
        )}
      </FlexBoxColumn>
    </Popup>
  );
};

export default MemberEntryMeanPopup;

MemberEntryMeanPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  subTitle: PropTypes.string,
  siteNumber: PropTypes.string,
  entryMeanCode: PropTypes.string,
  subsidiaryList: PropTypes.arrayOf(PropTypes.object),
};
